import { HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CoreListEnvelop,
  Credit,
  CreditCreateInput,
  CreditDto,
  CreditPatchBody,
  CreditRedemptionDto,
  CreditRedemptionState,
  CreditRedemptionStopBody,
  CreditState,
  DEPRECATED_PRODUCT_CODES,
  ExtendCreditInput,
  FREE_PRODUCT_CODES,
  ProductCode,
  SlotCode,
  TransferCreditInput,
  UUID,
} from '@mkp/shared/models';
import { getCurrentAvailableCreditsQuery, isFreeProductCode } from '@mkp/shared/util-product';
import { formatISO9075, startOfYear } from 'date-fns';
import { exhaustMap, map, Observable, of, switchMap } from 'rxjs';
import { BaseHttpResource, extractGetOptions, GetWithQueryOptions } from '../base-http.resource';
import { CreditRedemptionResource } from '../credit-redemption/credit-redemption.resource';
import { QueryParams } from '../query-params.model';

type PreviousCreditRedemptionData = Pick<CreditRedemptionDto, 'id' | '_version' | '_state'> & {
  productType: ProductCode | SlotCode;
};

const getExcludedProductFilter = (excludedProducts: ProductCode[]): string =>
  excludedProducts.map((product) => `product.type!=${product}`).join(';');

const getIncludedProductFilter = (includedProducts: readonly ProductCode[]): string =>
  includedProducts.map((product) => `product.type==${product}`).join(',');

@Injectable({ providedIn: 'root' })
export class CreditResource extends BaseHttpResource<
  CreditDto,
  CoreListEnvelop<CreditDto>,
  CreditCreateInput,
  CreditPatchBody
> {
  private readonly creditRedemptionResource = inject(CreditRedemptionResource);

  constructor() {
    super('credit');
  }

  override add(item: CreditCreateInput, context?: HttpContext): Observable<CreditDto> {
    return this.http
      .post<CreditDto>(this.url, item, { headers: new HttpHeaders({ maxRetry: '1' }), context })
      .pipe(map((dto) => this.mapDto(dto)));
  }

  /**
   * Get **ALL** credits; This is an override of the `getWithQuery` method ;
   * Be careful when using this method, it might return a large number of results
   * Returning all available and past credits (use filters to get specific results)
   * @override getWithQuery
   * @param paramsOrOptions
   */
  override getWithQuery(
    paramsOrOptions: Partial<QueryParams> | GetWithQueryOptions
  ): Observable<CoreListEnvelop<CreditDto>> {
    const { params, headers, context, withAccountId } = extractGetOptions(paramsOrOptions);
    params.filter = `${params.filter ? `${params.filter};` : ''}${getExcludedProductFilter([...FREE_PRODUCT_CODES, ...DEPRECATED_PRODUCT_CODES])}`;
    return super.getWithQuery({ params, headers, context, withAccountId });
  }

  getByExternalId({
    externalId,
    limit = 1,
  }: {
    externalId: CreditDto['externalId'];
    limit?: number;
  }): Observable<CoreListEnvelop<CreditDto>> {
    return super.getWithQuery({
      filter: `externalId==${externalId}`,
      limit,
    });
  }

  useCredit(
    accountId: string | UUID,
    vacancyId: string | UUID,
    product: ProductCode,
    previousData: Partial<PreviousCreditRedemptionData>,
    walletId?: string,
    startDate = 'now'
  ): Observable<unknown> {
    const now = formatISO9075(new Date());
    const baseFilter = `wallet.walletAccesses.account.id==${accountId};product.type==${product};_availableCredits>0;validTo>${now};validFrom<=${now}`;
    const filter = walletId ? `${baseFilter};wallet.id==${walletId}` : baseFilter;
    const sort = `validTo=asc`;

    const { id, _version, _state, productType } = previousData;
    const prior =
      id && _version && productType && _state === CreditRedemptionState.Active
        ? isFreeProductCode(productType)
          ? this.creditRedemptionResource.update(id, {
              id,
              _version,
              stoppedAt: 'now',
            } satisfies CreditRedemptionStopBody)
          : this.update(id, { id, _version, state: CreditState.Refund })
        : of({});

    return prior.pipe(
      switchMap(() =>
        super.getWithQuery({ limit: 1, filter, sort }).pipe(
          map((response) => response._embedded.results[0]),
          exhaustMap(({ id }) =>
            this.http.post(`${this.url}/${id}/redeem`, { vacancyId, start: startDate })
          )
        )
      )
    );
  }

  transferCredit(
    creditId: Credit['id'],
    { walletId, quantity }: TransferCreditInput
  ): Observable<unknown> {
    return this.http.post(`${this.url}/${creditId}/transfer`, {
      walletId,
      quantity,
    });
  }

  redeemSlot(slotId: string, vacancyId: string, startDate = 'now'): Observable<unknown> {
    return this.http.post(`${this.url}/${slotId}/redeem`, {
      vacancyId,
      start: startDate,
    });
  }

  // CDP-1773: start date should not be passed with client timestamp to avoid timezone issues
  redeemCredit(creditId: string, vacancyId: string, startDate = 'now'): Observable<unknown> {
    return this.http.post(`${this.url}/${creditId}/redeem`, {
      vacancyId,
      start: startDate,
    });
  }

  extendCredit(
    creditId: Credit['id'],
    { type, quantity, walletId }: ExtendCreditInput
  ): Observable<unknown> {
    return this.http.post(`${this.url}/${creditId}/extend`, {
      type,
      quantity,
      walletId,
    });
  }

  /**
   * Check if an account has active PPP (Pay Per Performance) credits
   * @param accountId The account ID to check
   * @returns Observable<boolean> true if the account has active PPP credits
   */
  hasActivePPPCredits(accountId: string | UUID): Observable<boolean> {
    return super
      .getWithQuery({
        params: getCurrentAvailableCreditsQuery(0, `;product.type==PAYPERPERFORMANCE`),
        withAccountId: accountId,
      })
      .pipe(map((response) => response.totalCount > 0));
  }

  hasUsedFreeCreditThisYear(accountId: string | UUID): Observable<boolean> {
    const filter = `${getIncludedProductFilter(FREE_PRODUCT_CODES)};validFrom>=${formatISO9075(startOfYear(new Date()))}`;
    return super
      .getWithQuery({
        params: { limit: 0, filter },
        withAccountId: accountId,
      })
      .pipe(map((response) => response.totalCount > 0));
  }

  cancel = (creditId: Credit['id']): Observable<unknown> =>
    this.http.post(`${this.url}/${creditId}/cancel`, {});
}
